// The app pages HMRC and Companies House redirect a sign-in to (their registered redirect URLs): /oauth/hmrc and
// /oauth/companies-house, with the provider's code and the sign-in state on the query. The signed-in user posts them
// to the provider's complete lambda, which only completes a sign-in for the user who started it, and only once.
// Pure: no DOM, so the unit tests can import it (the session storage helpers take the store as a parameter).

import { base64UrlEncode } from '../../helpers/common.js';

export const OAUTH_COMPLETE_LAMBDAS = {
  hmrc: 'hmrc/auth/complete',
  'companies-house': 'companies-house/auth/complete',
} as const;

export type OAuthProvider = keyof typeof OAUTH_COMPLETE_LAMBDAS;

export interface OAuthLanding {
  provider: OAuthProvider;
  lambdaPath: string;
  code: string;
  state: string;
  // The provider's error when the sign-in was declined or failed (OAuth's `error`, e.g. access_denied); '' otherwise
  error: string;
}

export const DIFFERENT_USER_MESSAGE =
  'This sign-in has to be finished by the person who started it, while signed in to nodeStream as themselves. Ask them to start it again from the card.';

function isProvider(value: string | undefined): value is OAuthProvider {
  return value !== undefined && Object.prototype.hasOwnProperty.call(OAUTH_COMPLETE_LAMBDAS, value);
}

// The provider of an /oauth/<provider> path, or null for any other path
export function oauthProviderOf(pathname: string): OAuthProvider | null {
  const [root, provider, ...rest] = pathname.split('/').filter((part) => part);
  if (root !== 'oauth' || rest.length > 0 || !isProvider(provider)) return null;
  return provider;
}

// What an /oauth/<provider> landing carries, or null when the URL is not one. A landing with no code or state still
// counts as one (code and state are then empty), so the page can say the sign-in cannot be finished.
export function readOAuthLanding(url: URL): OAuthLanding | null {
  const provider = oauthProviderOf(url.pathname);
  if (!provider) return null;
  return {
    provider,
    lambdaPath: OAUTH_COMPLETE_LAMBDAS[provider],
    code: url.searchParams.get('code') ?? '',
    state: url.searchParams.get('state') ?? '',
    error: url.searchParams.get('error') ?? '',
  };
}

// The landing URL with its query and fragment removed, so the code is in neither the address bar nor the history
export function withoutQuery(url: URL): string {
  return url.pathname;
}

// The referrer policy a landing page sets before anything else is fetched: the address carried the provider's code and the
// sign-in state, so no request the page makes may name it (ns-app adds it as <meta name="referrer">). Null off a landing.
export const LANDING_REFERRER_POLICY = 'no-referrer';
export function referrerPolicyFor(landing: OAuthLanding | null): typeof LANDING_REFERRER_POLICY | null {
  return landing ? LANDING_REFERRER_POLICY : null;
}

// The URL the complete lambda answered with, when it is a page of this app; anything else is refused
export function safeReturnUrl(returned: unknown, origin: string): string | null {
  if (typeof returned !== 'string' || !returned) return null;
  try {
    const target = new URL(returned, origin);
    return target.origin === origin ? target.toString() : null;
  } catch {
    return null;
  }
}

// Whether the complete lambda refused the caller as not the user who started the sign-in, or found no signed-in user
export function isDifferentUserRefusal(message: string): boolean {
  return /started by another user|calling user/i.test(message);
}

// The one landing this page load holds: taken once, so the code is posted at most once and kept nowhere else
let pending: OAuthLanding | null = null;

export function holdOAuthLanding(landing: OAuthLanding | null) {
  pending = landing;
}

// A declined or failed sign-in goes back to the card that started it. The card remembers itself in session storage just
// before it sends the browser to the provider; the landing takes it once. Nothing is posted to a lambda for an error.

export const OAUTH_START_KEY = 'ns-oauth-start';

// A start older than this is not taken back to (the sign-in was abandoned and a stray landing came later)
export const OAUTH_START_MAX_AGE_MS = 30 * 60 * 1000;

const PROVIDER_NAMES: Record<OAuthProvider, string> = { hmrc: 'HMRC', 'companies-house': 'Companies House' };

/** Remembers the card a sign-in was started from. Best effort: storage that is blocked just means no way back. */
export function rememberOAuthStart(
  provider: OAuthProvider,
  jobId: string,
  storage: Storage | undefined = globalThis.sessionStorage,
  now = Date.now(),
) {
  try {
    storage?.setItem(OAUTH_START_KEY, JSON.stringify({ provider, jobId, at: now }));
  } catch {
    // No session storage: the landing falls back to its own message
  }
}

/** The job the provider's sign-in was started from, taken once; null when none, another provider's, or too old. */
export function takeOAuthStart(
  provider: OAuthProvider,
  storage: Storage | undefined = globalThis.sessionStorage,
  now = Date.now(),
): string | null {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(OAUTH_START_KEY) ?? null;
    storage?.removeItem(OAUTH_START_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const start: unknown = JSON.parse(raw);
    if (typeof start !== 'object' || start === null) return null;
    const { provider: startedWith, jobId, at } = start as Record<string, unknown>;
    if (startedWith !== provider || typeof jobId !== 'string' || !/^[a-f0-9]{24}$/i.test(jobId)) return null;
    if (typeof at !== 'number' || now - at < 0 || now - at > OAUTH_START_MAX_AGE_MS) return null;
    return jobId;
  } catch {
    return null;
  }
}

/** What the card says after the provider sent the user back with an error, in our words (never the provider's text). */
export function declinedMessage(provider: OAuthProvider, error: string): string {
  const name = PROVIDER_NAMES[provider];
  if (error === 'access_denied') {
    return `You chose not to give nodeStream permission, so we couldn't connect to ${name}. Sign in again when you're ready.`;
  }
  return `${name} couldn't finish the sign-in. Please sign in again.`;
}

/**
 * The card to go back to with the error, in the form ns-app already reads (`?error=` base64url JSON into the job's error
 * store). The fields match the HMRC card's HMRCJobError; the Companies House card reads only the message.
 */
export function cardErrorUrl(provider: OAuthProvider, jobId: string, error: string): string {
  const payload = {
    message: declinedMessage(provider, error),
    hmrcCode: null,
    retryable: false,
    reauthRequired: false,
    declined: error === 'access_denied',
  };
  return `/feed/job/${encodeURIComponent(jobId)}?error=${base64UrlEncode(JSON.stringify(payload))}`;
}

export function takeOAuthLanding(): OAuthLanding | null {
  const landing = pending;
  pending = null;
  return landing;
}
